import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Users, Search, Lock, Unlock, CheckCircle2, Ban, History, FileText, Receipt, Loader2, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { PhotoThumb } from "../components/ImageUploader";

const STATUS_ACESSO = [
  { v: "ativo", label: "Ativo", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { v: "inativo", label: "Inativo", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  { v: "bloqueado", label: "Bloqueado", cls: "bg-red-100 text-red-800 border-red-300" },
];
const STATUS_OPER = [
  { v: "regular", label: "Regular", cls: "bg-emerald-100 text-emerald-800" },
  { v: "pendente", label: "Pendente", cls: "bg-amber-100 text-amber-800" },
  { v: "suspensa", label: "Suspensa", cls: "bg-red-100 text-red-800" },
];
const STATUS_FIN = [
  { v: "regular", label: "Regular", cls: "bg-emerald-100 text-emerald-800" },
  { v: "inadimplente", label: "Inadimplente", cls: "bg-red-100 text-red-800" },
  { v: "em_negociacao", label: "Em negociação", cls: "bg-amber-100 text-amber-800" },
];
const STATUS_CONTR = [
  { v: "ativo", label: "Ativo", cls: "bg-emerald-100 text-emerald-800" },
  { v: "encerrado", label: "Encerrado", cls: "bg-zinc-100 text-zinc-700" },
  { v: "sem_contrato", label: "Sem contrato", cls: "bg-blue-100 text-blue-800" },
];

const badge = (val, list) => {
  const it = list.find((x) => x.v === val);
  if (!it) return <Badge variant="outline">{val}</Badge>;
  return <Badge className={`${it.cls} border`}>{it.label}</Badge>;
};

export default function AdminClientes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [origemFiltro, setOrigemFiltro] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [tipoClienteFiltro, setTipoClienteFiltro] = useState("");
  const [ufFiltro, setUfFiltro] = useState("");
  const [totals, setTotals] = useState({ total: 0, total_loc: 0, total_crm: 0 });
  const [detalhe, setDetalhe] = useState(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (origemFiltro) params.origem = origemFiltro;
      if (statusFiltro) params.status_acesso = statusFiltro;
      if (tipoClienteFiltro) params.tipo_cliente = tipoClienteFiltro;
      if (ufFiltro) params.uf = ufFiltro;
      const { data } = await api.get("/admin/clientes", { params });
      setItems(data.items || []);
      setTotals({ total: data.total, total_loc: data.total_locativo, total_crm: data.total_crm });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(carregar, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, origemFiltro, statusFiltro, tipoClienteFiltro, ufFiltro]);

  const kpis = useMemo(() => {
    const ativos = items.filter((x) => x.status_acesso === "ativo").length;
    const inativos = items.filter((x) => x.status_acesso === "inativo").length;
    const bloqueados = items.filter((x) => x.status_acesso === "bloqueado").length;
    return { ativos, inativos, bloqueados };
  }, [items]);

  return (
    <div className="space-y-5" data-testid="admin-clientes-page">
      <div className="flex items-center gap-3">
        <Users size={28} style={{ color: "#1B3A6B" }} />
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Administração de Clientes
          </h1>
          <p className="text-sm text-muted-foreground">
            Gestão unificada — base operacional ({totals.total_loc}) + CRM ({totals.total_crm})
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 border-l-4 border-l-blue-500">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-2xl font-bold" data-testid="kpi-total">{totals.total}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-emerald-500">
          <div className="text-xs text-muted-foreground">Ativos (página)</div>
          <div className="text-2xl font-bold text-emerald-700" data-testid="kpi-ativos">{kpis.ativos}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-zinc-400">
          <div className="text-xs text-muted-foreground">Inativos (página)</div>
          <div className="text-2xl font-bold text-zinc-700" data-testid="kpi-inativos">{kpis.inativos}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-red-500">
          <div className="text-xs text-muted-foreground">Bloqueados (página)</div>
          <div className="text-2xl font-bold text-red-700" data-testid="kpi-bloqueados">{kpis.bloqueados}</div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Buscar (nome, CNPJ, e-mail)</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-3 text-muted-foreground" />
              <Input
                className="pl-8"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ex: WMS, 93.209..."
                data-testid="admin-clientes-busca"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Origem</Label>
            <Select value={origemFiltro || "_all"} onValueChange={(v) => setOrigemFiltro(v === "_all" ? "" : v)}>
              <SelectTrigger data-testid="filtro-origem"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todas</SelectItem>
                <SelectItem value="locativo">Base Operacional</SelectItem>
                <SelectItem value="crm">CRM</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tipo Cliente</Label>
            <Select value={tipoClienteFiltro || "_all"} onValueChange={(v) => setTipoClienteFiltro(v === "_all" ? "" : v)}>
              <SelectTrigger data-testid="filtro-tipo-cliente"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todos</SelectItem>
                <SelectItem value="ambos">Ambos</SelectItem>
                <SelectItem value="helpdesk">Helpdesk</SelectItem>
                <SelectItem value="locativo">Locativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">UF</Label>
            <Select value={ufFiltro || "_all"} onValueChange={(v) => setUfFiltro(v === "_all" ? "" : v)}>
              <SelectTrigger data-testid="filtro-uf"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todas</SelectItem>
                {["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"].map((uf) => (
                  <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFiltro || "_all"} onValueChange={(v) => setStatusFiltro(v === "_all" ? "" : v)}>
              <SelectTrigger data-testid="filtro-status"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todos</SelectItem>
                {STATUS_ACESSO.map((s) => (
                  <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">LOGO</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>CNPJ/CPF</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>Acesso</TableHead>
              <TableHead>Operacional</TableHead>
              <TableHead>Financeiro</TableHead>
              <TableHead>Contratual</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={11} className="text-center py-8">
                <Loader2 className="animate-spin inline" /> Carregando...
              </TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                Nenhum cliente encontrado.
              </TableCell></TableRow>
            )}
            {!loading && items.map((c) => (
              <TableRow key={c.id} data-testid={`admin-cli-row-${c.id}`}>
                <TableCell><PhotoThumb photos={c.fotos} size={36} alt={c.nome} /></TableCell>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell className="text-xs font-mono">{c.cnpj_cpf || "—"}</TableCell>
                <TableCell>
                  {c.tipo_cliente ? (
                    <Badge variant="outline" className={
                      c.tipo_cliente === "ambos" ? "bg-orange-50 text-orange-700 border-orange-200" :
                      c.tipo_cliente === "locativo" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      c.tipo_cliente === "helpdesk" ? "bg-violet-50 text-violet-700 border-violet-200" :
                      ""
                    }>
                      {c.tipo_cliente.toUpperCase()}
                    </Badge>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    <Building2 size={10} className="mr-1" />
                    {c.origem === "locativo" ? "Base" : "CRM"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{c.uf || "—"}</TableCell>
                <TableCell>{badge(c.status_acesso, STATUS_ACESSO)}</TableCell>
                <TableCell>{badge(c.status_operacional, STATUS_OPER)}</TableCell>
                <TableCell>{badge(c.status_financeiro, STATUS_FIN)}</TableCell>
                <TableCell>{badge(c.status_contratual, STATUS_CONTR)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/app/admin/clientes/${c.id}`)}
                    data-testid={`abrir-detalhe-${c.id}`}
                  >
                    Gerenciar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {detalhe && (
        <DetalheCliente
          cliente={detalhe}
          onClose={() => setDetalhe(null)}
          onSaved={() => { setDetalhe(null); carregar(); }}
        />
      )}
    </div>
  );
}

// ============================================================
function DetalheCliente({ cliente, onClose, onSaved }) {
  const [form, setForm] = useState({
    status_acesso: cliente.status_acesso,
    status_operacional: cliente.status_operacional,
    status_financeiro: cliente.status_financeiro,
    status_contratual: cliente.status_contratual,
    motivo: "",
  });
  const [busy, setBusy] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [propostas, setPropostas] = useState([]);

  useEffect(() => {
    const carregarExtras = async () => {
      try {
        const [h, c, p] = await Promise.all([
          api.get(`/admin/clientes/${cliente.id}/historico`),
          api.get(`/admin/clientes/${cliente.id}/contratos`),
          api.get(`/admin/clientes/${cliente.id}/propostas`),
        ]);
        setHistorico(h.data?.items || []);
        setContratos(c.data?.items || []);
        setPropostas(p.data?.items || []);
      } catch {
        // silencioso
      }
    };
    carregarExtras();
  }, [cliente.id]);

  const salvar = async () => {
    const changed = {};
    ["status_acesso", "status_operacional", "status_financeiro", "status_contratual"].forEach((k) => {
      if (form[k] && form[k] !== cliente[k]) changed[k] = form[k];
    });
    if (Object.keys(changed).length === 0) {
      toast.info("Nenhuma alteração para salvar");
      return;
    }
    if (changed.status_acesso === "bloqueado" && !form.motivo.trim()) {
      toast.error("Informe um motivo para bloquear o cliente");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/clientes/${cliente.id}/status`, { ...changed, motivo: form.motivo || null });
      toast.success("Cliente atualizado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setBusy(false);
    }
  };

  const acaoRapida = async (novoStatus) => {
    if (novoStatus === "bloqueado") {
      setForm({ ...form, status_acesso: "bloqueado" });
      toast.info("Informe o motivo abaixo e clique em Salvar");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/clientes/${cliente.id}/status`, { status_acesso: novoStatus });
      toast.success(`Cliente ${novoStatus === "ativo" ? "ativado" : "desativado"}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="detalhe-cliente-dialog">
        <DialogHeader>
          <DialogTitle>{cliente.nome}</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs">{cliente.cnpj_cpf}</span> ·
            Origem: <Badge variant="outline" className="ml-1">{cliente.origem === "locativo" ? "Base Operacional" : "CRM"}</Badge>
          </DialogDescription>
        </DialogHeader>

        {/* Ações rápidas */}
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant={cliente.status_acesso === "ativo" ? "default" : "outline"}
            onClick={() => acaoRapida("ativo")}
            disabled={busy || cliente.status_acesso === "ativo"}
            className={cliente.status_acesso === "ativo" ? "text-white" : ""}
            style={cliente.status_acesso === "ativo" ? { background: "#16a34a" } : {}}
            data-testid="btn-ativar"
          >
            <CheckCircle2 size={14} className="mr-1" /> Ativar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => acaoRapida("inativo")}
            disabled={busy || cliente.status_acesso === "inativo"}
            data-testid="btn-desativar"
          >
            <Lock size={14} className="mr-1" /> Desativar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => acaoRapida("bloqueado")}
            disabled={busy || cliente.status_acesso === "bloqueado"}
            data-testid="btn-bloquear"
          >
            <Ban size={14} className="mr-1" /> Bloquear acesso ao Portal
          </Button>
          {cliente.status_acesso === "bloqueado" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => acaoRapida("ativo")}
              disabled={busy}
              data-testid="btn-desbloquear"
            >
              <Unlock size={14} className="mr-1" /> Desbloquear
            </Button>
          )}
        </div>

        <Tabs defaultValue="status" className="mt-4">
          <TabsList>
            <TabsTrigger value="status" data-testid="tab-status">Status</TabsTrigger>
            <TabsTrigger value="contratos" data-testid="tab-contratos">Contratos ({contratos.length})</TabsTrigger>
            <TabsTrigger value="propostas" data-testid="tab-propostas">Propostas ({propostas.length})</TabsTrigger>
            <TabsTrigger value="historico" data-testid="tab-historico">Histórico ({historico.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="status" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Acesso</Label>
                <Select value={form.status_acesso} onValueChange={(v) => setForm({ ...form, status_acesso: v })}>
                  <SelectTrigger data-testid="sel-status-acesso"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_ACESSO.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Situação Operacional</Label>
                <Select value={form.status_operacional} onValueChange={(v) => setForm({ ...form, status_operacional: v })}>
                  <SelectTrigger data-testid="sel-status-oper"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPER.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Situação Financeira</Label>
                <Select value={form.status_financeiro} onValueChange={(v) => setForm({ ...form, status_financeiro: v })}>
                  <SelectTrigger data-testid="sel-status-fin"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_FIN.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Situação Contratual</Label>
                <Select value={form.status_contratual} onValueChange={(v) => setForm({ ...form, status_contratual: v })}>
                  <SelectTrigger data-testid="sel-status-contr"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_CONTR.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Motivo (obrigatório ao bloquear)</Label>
                <Textarea
                  rows={2}
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                  placeholder="Ex: Inadimplência > 90 dias"
                  data-testid="input-motivo"
                />
              </div>
            </div>
            {form.status_acesso === "bloqueado" && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                <Ban size={12} className="inline mr-1" /> Cliente bloqueado: acesso ao Portal Cliente será negado.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={salvar} disabled={busy} className="text-white" style={{ background: "#1B3A6B" }} data-testid="btn-salvar-status">
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Salvar alterações
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="contratos" className="pt-3">
            {contratos.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Sem contratos vinculados.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Início</TableHead>
                    <TableHead>Término</TableHead>
                    <TableHead className="text-right">Valor 1ª fatura</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contratos.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.tipo}</TableCell>
                      <TableCell>{c.data_inicio}</TableCell>
                      <TableCell>{c.data_termino}</TableCell>
                      <TableCell className="text-right">
                        {Number(c.valor_primeira_fatura || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </TableCell>
                      <TableCell>{c.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="propostas" className="pt-3">
            {propostas.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Sem propostas vinculadas.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propostas.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs">{p.numero || "—"}</TableCell>
                      <TableCell>{(p.criado_em || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-right">
                        {Number(p.valor_total || p.valor_mensal_total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </TableCell>
                      <TableCell>{p.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="historico" className="pt-3">
            {historico.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Sem alterações registradas.</p>
            ) : (
              <div className="space-y-2">
                {historico.map((h) => (
                  <Card key={h.id} className="p-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold">{h.usuario_nome || h.usuario_email}</span>
                      <span className="text-muted-foreground">{(h.criado_em || "").slice(0, 19).replace("T", " ")}</span>
                    </div>
                    <div className="mt-1 text-xs font-mono">
                      {Object.entries(h.alteracoes || {}).map(([k, v]) => (
                        <div key={k}>
                          <strong>{k}:</strong> {String(v)}
                        </div>
                      ))}
                    </div>
                    {h.motivo && (
                      <div className="mt-1 text-xs italic text-zinc-600">Motivo: {h.motivo}</div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
